import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import PaywallModal from '../components/PaywallModal';
import { downloadApi } from '../services/api';
import { Download, Lock, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';

const DOWNLOADS = [
  {
    fileKey: 'checklist-a4',
    filename: 'FSA_Fenix_A320_Checklist_Rev3.1_A4.pdf',
    title: 'Fenix A320 Checklist - all 21 phases (Rev 3.1, Fenix V3)',
    description: 'All 220 checks from cold and dark to secured, with a short note on every item. A4, print-friendly.',
  },
  {
    fileKey: 'checklist-a5',
    filename: 'FSA_Fenix_A320_Checklist_Rev3.1_A5.pdf',
    title: 'Kneeboard checklist (A5, Rev 3.1)',
    description: 'Compact A5 layout, sized for a kneeboard.',
  },
];

const DownloadsPage = (props) => {
  const auth = useAuth();
  const user = auth.user;
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [extraDownloads, setExtraDownloads] = useState([]);

  // Fetch the MCDU Guide + Quick Reference Cards downloads (added
  // 2026-10-15) and show them under the existing checklist downloads.
  useEffect(() => {
    if (!user?.has_full_access) return;
    downloadApi.getList()
      .then((res) => {
        const mapped = (res.data.downloads || []).map((d) => ({
          fileKey: d.id,
          filename: d.filename,
          title: d.title,
          description: d.description,
        }));
        setExtraDownloads(mapped);
      })
      .catch((err) => console.error('Failed to load additional downloads:', err));
  }, [user?.has_full_access]);

  const allDownloads = [...DOWNLOADS, ...extraDownloads];

  const handleDownload = async (download) => {
    setDownloadingKey(download.fileKey);
    try {
      const response = await downloadApi.getFile(download.fileKey);
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = download.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('Download failed. Please try again.');
    } finally {
      setDownloadingKey(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="downloads-page">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-cockpit-text-primary">Downloads</h1>
          <p className="text-sm text-cockpit-text-secondary">Printable checklists for the Fenix A320 Edition</p>
        </div>

        {user?.has_full_access ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="downloads-unlocked">
            {allDownloads.map((download) => (
              <div key={download.fileKey} className="card-panel p-6 flex flex-col" data-testid={`download-card-${download.fileKey}`}>
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-4 h-4 text-fsa-blue" />
                  <h2 className="text-sm font-medium text-cockpit-text-primary">{download.title}</h2>
                </div>
                <p className="text-sm text-cockpit-text-secondary flex-1 mb-4">{download.description}</p>
                <button
                  onClick={() => handleDownload(download)}
                  disabled={downloadingKey === download.fileKey}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid={`download-button-${download.fileKey}`}
                >
                  {downloadingKey === download.fileKey ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Download
                </button>
                <p className="text-xs text-cockpit-text-secondary/60 mt-3 text-center">
                  Personal licence, please don't share.
                </p>
              </div>
            ))}
          </div>
        ) : (
          <button
            onClick={() => setShowPaywall(true)}
            className="card-panel p-8 w-full text-center hover:border-fsa-blue/40 transition-colors"
            data-testid="downloads-locked"
          >
            <div className="w-14 h-14 bg-cockpit-bg border border-cockpit-border rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-6 h-6 text-cockpit-text-secondary" />
            </div>
            <h2 className="text-lg font-medium text-cockpit-text-primary mb-2">Downloads are a paid feature</h2>
            <p className="text-sm text-cockpit-text-secondary">
              Unlock Fenix A320 Edition to download the printable kneeboard and A4 checklists.
            </p>
          </button>
        )}
      </div>

      <PaywallModal isOpen={showPaywall} onClose={() => setShowPaywall(false)} />
    </DashboardLayout>
  );
};

export default DownloadsPage;
